import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type { CustomerAccountRole, CustomerOption, ExceptionField } from '@/api/cockpit';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  formatFieldDateLabel,
  formatMatchedSelectOptionLabel,
  isCustomerAccountField,
  isDateField,
  isMatchedHeaderField,
  isSimpleMappedSelectField,
  parseIsoDate,
  resolveFieldValue,
  resolveMatchedFieldLabel,
  resolveMatchedFieldValue,
} from '../helpers';
import { CustomerAccountPicker } from './CustomerAccountPicker';

export interface CustomerPickerState {
  searchValue: string;
  debouncedSearchValue: string;
  onSearchChange: (value: string, role: CustomerAccountRole) => void;
  catalogOptions: readonly CustomerOption[];
  relatedBillToSelection?: CustomerOption | null;
  onCustomerAccountSelect?: (role: CustomerAccountRole, option: CustomerOption) => void;
}

export interface FieldValueEditorProps {
  field: ExceptionField;
  fields: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  disabled: boolean;
  customerPicker: CustomerPickerState;
}

export function FieldValueEditor(props: FieldValueEditorProps): JSX.Element {
  const { field, fields, onFieldChange, disabled, customerPicker } = props;
  if (isMatchedHeaderField(field)) {
    const isBillToAccount = field.key === 'invoice_customer_number';
    return (
      <div className="space-y-2">
        {!isBillToAccount ? (
          <div className="flex min-w-0 items-baseline gap-2 rounded border border-border/70 bg-background/60 px-2.5 py-1.5">
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Extracted
            </span>
            <span
              className="min-w-0 truncate text-xs text-foreground"
              title={field.extractedValue ?? ''}
            >
              {field.extractedValue || 'Not provided'}
            </span>
          </div>
        ) : null}
        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {isBillToAccount ? 'Selected' : 'Matched'}
          </div>
          <MatchedValueInput
            field={field}
            fields={fields}
            onFieldChange={onFieldChange}
            disabled={disabled}
            customerPicker={customerPicker}
          />
        </div>
      </div>
    );
  }
  if (isDateField(field)) {
    return (
      <DateFieldPicker
        value={resolveFieldValue(field, fields)}
        disabled={disabled}
        onChange={(iso) => onFieldChange(field.key, iso)}
      />
    );
  }
  return (
    <Input
      id={field.key}
      defaultValue={field.value}
      title={field.value}
      disabled={disabled}
      onChange={(e) => (disabled ? undefined : onFieldChange(field.key, e.target.value))}
    />
  );
}

function MatchedValueInput(props: FieldValueEditorProps): JSX.Element {
  const { field, fields, onFieldChange, disabled, customerPicker } = props;
  if (isCustomerAccountField(field)) {
    const role: CustomerAccountRole =
      field.key === 'invoice_customer_number' ? 'bill_to' : 'ship_to';
    return (
      <CustomerAccountPicker
        role={role}
        field={field}
        fields={fields}
        onFieldChange={onFieldChange}
        disabled={disabled}
        searchValue={customerPicker.searchValue}
        debouncedSearchValue={customerPicker.debouncedSearchValue}
        onSearchChange={(value) => customerPicker.onSearchChange(value, role)}
        catalogOptions={customerPicker.catalogOptions}
        {...(customerPicker.relatedBillToSelection !== undefined
          ? { relatedBillToSelection: customerPicker.relatedBillToSelection }
          : {})}
        {...(customerPicker.onCustomerAccountSelect
          ? { onCustomerAccountSelect: customerPicker.onCustomerAccountSelect }
          : {})}
      />
    );
  }
  if (isSimpleMappedSelectField(field)) {
    return (
      <Select
        value={resolveMatchedFieldValue(field, fields)}
        onValueChange={(value) => {
          if (disabled) return;
          onFieldChange(field.key, value);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          id={field.key}
          title={resolveMatchedFieldLabel(field, fields)}
          className="bg-background"
        >
          <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {(field.matchedOptions ?? []).map((option) => (
            <SelectItem key={`${field.key}:${option.value}`} value={option.value}>
              {formatMatchedSelectOptionLabel(field, option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (isDateField(field)) {
    return (
      <DateFieldPicker
        value={resolveMatchedFieldValue(field, fields)}
        disabled={disabled}
        onChange={(iso) => onFieldChange(field.key, iso)}
      />
    );
  }
  return (
    <Input
      id={field.key}
      defaultValue={field.matchedDisplayValue ?? field.matchedValue ?? field.value}
      title={field.matchedDisplayValue ?? field.matchedValue ?? field.value}
      disabled={disabled}
      onChange={(e) => (disabled ? undefined : onFieldChange(field.key, e.target.value))}
    />
  );
}

function DateFieldPicker(props: {
  value: string;
  disabled: boolean;
  onChange: (iso: string) => void;
}): JSX.Element {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'w-full justify-between text-left font-normal',
            !props.value && 'text-muted-foreground',
          )}
          disabled={props.disabled}
          title={props.value}
        >
          {formatFieldDateLabel(props.value)}
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parseIsoDate(props.value)}
          onSelect={(date) => {
            if (props.disabled) return;
            if (!date) return;
            props.onChange(format(date, 'yyyy-MM-dd'));
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
